import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";

interface Req {
  id: string;
  registration_id: string;
  member_id: string | null;
  region: string;
  scope: string;
  requested_by_name: string;
  requested_by_email: string;
  reason: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_by_name?: string | null;
  reviewed_by_email?: string | null;
  reviewed_at?: string | null;
  members_removed?: number | null;
  registration_snapshot?: any;
}

interface Props { region?: string }
export const CancellationRequestsManager = ({ region }: Props = {}) => {
  const [loading, setLoading] = useState(true);
  const [reqs, setReqs] = useState<Req[]>([]);
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [notesById, setNotesById] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    let q = (supabase as any)
      .from("registration_cancellation_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (region) {
      const base = region.replace(/\s*Region\s*$/i, "").trim();
      q = q.or(`region.ilike.${base},region.ilike.${base} Region`);
    }
    const { data, error } = await q;
    if (error) toast({ title: "Failed to load", description: error.message, variant: "destructive" });
    setReqs((data || []) as Req[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [region]);

  const review = async (r: Req, status: "approved" | "rejected") => {
    setBusy(r.id);
    const { data: { user } } = await supabase.auth.getUser();

    // Resolve reviewer profile for audit trail
    let reviewerName: string | null = user?.email || null;
    let reviewerEmail: string | null = user?.email || null;
    if (user?.id) {
      const { data: au } = await (supabase as any)
        .from("admin_users")
        .select("full_name, user_name, email")
        .eq("auth_user_id", user.id)
        .maybeSingle();
      if (au) {
        reviewerName = au.full_name || au.user_name || au.email || reviewerName;
        reviewerEmail = au.email || reviewerEmail;
      }
    }

    // Snapshot + impact (only meaningful when approving)
    let snapshot: any = null;
    let membersRemoved = 0;
    if (status === "approved") {
      const { data: reg } = await (supabase as any)
        .from("conference_registrations")
        .select("*")
        .eq("id", r.registration_id)
        .maybeSingle();
      const { data: members } = await (supabase as any)
        .from("registration_members")
        .select("*")
        .eq("registration_id", r.registration_id);
      snapshot = { registration: reg, members: members || [] };

      if (r.scope === "member" && r.member_id) {
        const m = (members || []).find((x: any) => x.id === r.member_id);
        membersRemoved = m ? 1 : 0;
      } else {
        membersRemoved = reg?.total_members ?? (members?.length || 0);
      }
    }

    const { error } = await (supabase as any)
      .from("registration_cancellation_requests")
      .update({
        status,
        admin_notes: notesById[r.id] || null,
        reviewed_by: user?.id || null,
        reviewed_by_name: reviewerName,
        reviewed_by_email: reviewerEmail,
        reviewed_at: new Date().toISOString(),
        members_removed: membersRemoved,
        registration_snapshot: snapshot,
      })
      .eq("id", r.id);

    if (!error && status === "approved") {
      if (r.scope === "member" && r.member_id) {
        await (supabase as any).from("registration_members").delete().eq("id", r.member_id);
      } else {
        await (supabase as any).from("registration_members").delete().eq("registration_id", r.registration_id);
        await (supabase as any).from("conference_registrations").delete().eq("id", r.registration_id);
      }
    }

    setBusy(null);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({
      title: `Request ${status}`,
      description: status === "approved"
        ? `${membersRemoved} member${membersRemoved === 1 ? "" : "s"} removed from totals. Analytics & CSV exports updated.`
        : undefined,
    });
    load();
  };

  const filtered = filter === "all" ? reqs : reqs.filter(r => r.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">Cancellation Requests</h2>
          <p className="text-sm text-muted-foreground">Review and approve last-minute cancellations submitted by registrants.</p>
        </div>
        <div className="flex gap-2">
          {(["pending","approved","rejected","all"] as const).map(f => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}>
              {f[0].toUpperCase() + f.slice(1)}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">No {filter === "all" ? "" : filter} cancellation requests.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <Card key={r.id}>
              <CardHeader className="pb-2 flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="text-base">{r.requested_by_name}</CardTitle>
                  <p className="text-xs text-muted-foreground">{r.requested_by_email} · {r.region} · {new Date(r.created_at).toLocaleString()}</p>
                </div>
                <Badge variant={r.status === "pending" ? "secondary" : r.status === "approved" ? "default" : "destructive"}>
                  {r.status}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <strong>Scope:</strong> {r.scope === "family" ? "Whole family" : "Specific member"}
                  {r.member_id && <span className="text-muted-foreground"> ({r.member_id.slice(0,8)}…)</span>}
                </div>
                {r.reason && <div className="text-sm bg-muted/40 rounded p-2"><strong>Reason:</strong> {r.reason}</div>}
                {r.status === "pending" && (
                  <>
                    <Textarea
                      placeholder="Optional admin notes (visible in audit only)"
                      value={notesById[r.id] || ""}
                      onChange={e => setNotesById(s => ({ ...s, [r.id]: e.target.value }))}
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={busy === r.id} onClick={() => review(r, "approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve & cancel
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => review(r, "rejected")}>
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </>
                )}
                {r.admin_notes && r.status !== "pending" && (
                  <div className="text-xs text-muted-foreground"><strong>Admin notes:</strong> {r.admin_notes}</div>
                )}
                {r.status !== "pending" && (r.reviewed_by_name || r.reviewed_at) && (
                  <div className="text-xs rounded border bg-muted/30 p-2 space-y-0.5">
                    <div>
                      <strong>{r.status === "approved" ? "Approved" : "Rejected"} by:</strong>{" "}
                      {r.reviewed_by_name || "—"}
                      {r.reviewed_by_email ? ` (${r.reviewed_by_email})` : ""}
                    </div>
                    {r.reviewed_at && (
                      <div><strong>When:</strong> {new Date(r.reviewed_at).toLocaleString()}</div>
                    )}
                    {r.status === "approved" && typeof r.members_removed === "number" && (
                      <div>
                        <strong>Impact:</strong> {r.members_removed} member{r.members_removed === 1 ? "" : "s"} removed —
                        reflected in live ribbon counts and all CSV / analytics exports.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CancellationRequestsManager;
